import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  Button,
  IconButton,
  Alert,
  CircularProgress,
  Grid
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { API_ENDPOINTS } from '../../../../config/api';

const ApplicationTypeList = () => {
  const navigate = useNavigate();
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedTypeSettings, setSelectedTypeSettings] = useState([]);
  const [settingsLoading, setSettingsLoading] = useState(false);

  useEffect(() => {
    fetchApplicationTypes();
  }, []);

  const fetchApplicationTypes = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      if (!token) {
        navigate('/login');
        return;
      }

      const response = await axios.get(API_ENDPOINTS.APPLICATION_TYPES.BASE, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      setTypes(response.data.data || []);
      setError(null);
    } catch (error) {
      console.error('Error fetching application types:', error);
      setError('Failed to fetch application types. ' + (error.response?.data?.message || ''));
    } finally {
      setLoading(false);
    }
  };

  const fetchSettingsByType = async (id) => {
    try {
      setSettingsLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await axios.get(`${API_ENDPOINTS.APPLICATION_TYPES.BASE}/${id}/settings`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      // Assuming response.data.data contains { type, settings }
      setSelectedTypeSettings(response.data.data.settings || []);
      setError(null);
    } catch (error) {
      console.error('Error fetching application settings:', error);
      setError('Failed to fetch application settings. ' + (error.response?.data?.message || ''));
    } finally {
      setSettingsLoading(false);
    }
  };
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this application type?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      
      await axios.delete(`${API_ENDPOINTS.APPLICATION_TYPES.BASE}/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      // Refresh the list after deletion
      fetchApplicationTypes();
    } catch (error) {
      console.error('Error deleting application type:', error);
      setError('Failed to delete application type. ' + (error.response?.data?.message || ''));
    }
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5">
             &nbsp;
            </Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => navigate('/application-setting-types/create')}
            >
              Add New Type
            </Button>
          </Box>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : types.length === 0 ? (
            <Alert severity="info">No application types found</Alert>
          ) : (
            <TableContainer>
              <Table sx={{ minWidth: 650 }} aria-label="application types table">
                <TableHead>
                  <TableRow>
                    <TableCell>S.No</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Slug</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {types
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((type, index) => (
                      <TableRow key={type.id}>
                        <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                        <TableCell>{type.type}</TableCell>
                        <TableCell>{type.slug}</TableCell>
                        <TableCell align="right">

                          <IconButton
                            color="primary"
                            onClick={() => navigate(`/application-setting-types/edit/${type.id}`)}
                            title="Edit"
                          >
                            <EditIcon />
                          </IconButton> 

                          <IconButton 
                            color="error" 
                            onClick={() => handleDelete(type.id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={types.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </TableContainer>
          )}

{settingsLoading ? (
  <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
    <CircularProgress />
  </Box>
) : selectedTypeSettings.length > 0 && (
  <Box sx={{ mt: 3 }}>
    <Typography variant="h6">Application Settings</Typography>
    <TableContainer>
      <Table sx={{ minWidth: 650 }} aria-label="application settings table">
        <TableHead>
          <TableRow>
            <TableCell>ID</TableCell>
            <TableCell>Setting Name</TableCell>
            <TableCell>Value</TableCell>
            <TableCell>Description</TableCell>
            <TableCell>Sort Order</TableCell>
            <TableCell>Application Type</TableCell>
            <TableCell>Slug</TableCell>
            <TableCell>Created At</TableCell>
            <TableCell>Updated At</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {selectedTypeSettings.map((setting) => (
            <TableRow key={setting.id}>
              <TableCell>{setting.id}</TableCell>
              <TableCell>{setting.name}</TableCell>
              <TableCell>{setting.value}</TableCell>
              <TableCell>{setting.description || 'N/A'}</TableCell>
              <TableCell>{setting.sort}</TableCell>
              <TableCell>{setting.application_setting_type?.type || 'N/A'}</TableCell>
              <TableCell>{setting.application_setting_type?.slug || 'N/A'}</TableCell>
              <TableCell>{new Date(setting.created_at).toLocaleDateString()}</TableCell>
              <TableCell>{new Date(setting.updated_at).toLocaleDateString()}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  </Box>
)}
        </Paper>
      </Grid>
    </Grid>
  );
};

export default ApplicationTypeList;
